import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { Mail, MessageSquare, ListChecks, BookOpen, ArrowUpRight } from "lucide-react";

const features = [
  {
    id: "recap",
    icon: Mail,
    tag: "01",
    title: "Recap Email",
    copy: "A ready-to-send recap of your all-hands, drafted for leadership to send to the whole company.",
    span: "lg:col-span-3",
    style: "border border-line bg-paper text-walnut",
    iconStyle: "bg-terracotta/15 text-terracotta",
    copyStyle: "text-umber/85",
  },
  {
    id: "slack",
    icon: MessageSquare,
    tag: "02",
    title: "Slack-Ready Highlights",
    copy: "Short posts your team can read in seconds — text you can paste straight into your channels.",
    span: "lg:col-span-2",
    style: "bg-sage text-paper",
    iconStyle: "bg-paper/20 text-paper",
    copyStyle: "text-paper/85",
  },
  {
    id: "decisions",
    icon: ListChecks,
    tag: "03",
    title: "Decisions & Next Steps",
    copy: "A clear list of decisions and next steps, with owners and due dates.",
    span: "lg:col-span-2",
    style: "bg-walnut text-cream",
    iconStyle: "bg-cream/10 text-ochre",
    copyStyle: "text-cream/75",
  },
  {
    id: "keepsake",
    icon: BookOpen,
    tag: "Add-on",
    title: "Optional: Digital Magazine Keepsake",
    copy: "8 pages, people spotlights, the quarter in review — a keepsake version of the quarter, not the main event.",
    span: "lg:col-span-3",
    style: "border border-dashed border-walnut/30 bg-paper/50 text-walnut",
    iconStyle: "bg-terracotta/15 text-terracotta",
    copyStyle: "text-umber/85",
  },
];

export const Features = () => (
  <section id="features" data-testid="features-section" className="bg-paper py-28 lg:py-36">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-terracotta">
          What we deliver
        </p>
        <h2 className="mt-5 max-w-2xl font-serif text-4xl font-medium leading-tight tracking-tight text-walnut sm:text-5xl">
          What you get after every all-hands.
        </h2>
      </Reveal>

      <div className="mt-16 grid gap-6 lg:grid-cols-5">
        {features.map((f, i) => (
          <motion.div
            key={f.id}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -8 }}
            data-testid={`feature-card-${f.id}`}
            className={`group rounded-3xl p-9 shadow-[0_10px_30px_rgba(42,32,28,0.06)] ${f.span} ${f.style}`}
          >
            <div className="flex items-start justify-between">
              <span className={`flex h-12 w-12 items-center justify-center rounded-full ${f.iconStyle}`}>
                <f.icon className="h-5 w-5" />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-60">
                {f.tag}
              </span>
            </div>
            <h3 className="mt-8 flex items-center gap-2 font-serif text-2xl font-medium tracking-tight">
              {f.title}
              <ArrowUpRight className="h-4 w-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </h3>
            <p className={`mt-4 text-base leading-relaxed ${f.copyStyle}`}>{f.copy}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
